import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogContent,
} from '@angular/material/dialog';
import { getCurrencySymbol } from '../../../../../utils/currency-symbol.util';
import { ChangePlanConfirmationComponent } from '../change-plan-confirmation/change-plan-confirmation.component';
import { ToastrService } from 'ngx-toastr';
import {
  PlanPriceLike,
  PriceLabel,
  isPaidPrice,
  planChangeDirection,
  priceLabel,
} from '../../../../../utils/plan-pricing.util';
import {
  menuTitlesAddedByPlan,
  menuTitlesLockedForPlan,
  planFeatureLabels,
} from '../../../../../utils/subscription-plan-menu.util';
interface Plan {
  planId: string;
  name: string;
  isActive: boolean;
  /** Highlighted with a "Recommended" badge — see buildPlans() for why this is
   * name-keyed rather than data-driven. */
  isRecommended: boolean;
  description: string;
  originalPrice?: number;
  price: number;
  /** "$0.50" + "/order" + cap detail for per-order prices; "$49.99" + "/mon" for flat. */
  priceLabel: PriceLabel;
  /** Whether choosing it can lead to a charge, so a card is needed first (FNF-1286). */
  isPaid: boolean;
  /** A flat paid upgrade is prorated and charged now; per-order plans bill after the month. */
  prorated: boolean;
  /** The plan a scheduled downgrade will switch to at period end (FNF-1297). */
  isPendingTarget: boolean;
  promo?: { title: string; text: string };
  /** What this plan adds over the one below: the plan's own API features, then
   * the dashboard menus it unlocks (from subscription-plan-menu.util, the same
   * lists that lock the sidebar and routes). */
  features: string[];
  /** Name of the next-lower plan, for an "Everything in X, plus:" label. Undefined
   * for the lowest tier — nothing sits below it to inherit from. */
  previousPlanName?: string;
  buttonLabel: string;
  /** 'downgrade' is a quiet outline so it doesn't read like the upgrade action. */
  buttonStyle: 'outline' | 'dark' | 'downgrade';
  buttonDisabled: boolean;
  /** Whether selecting this (non-active) plan is an upgrade (applies now) or a
   * downgrade (scheduled for the end of the billing period, FNF-1297). Undefined
   * for the active plan itself, which has no timing to show. */
  timing?: 'upgrade' | 'downgrade';
}

interface ApiPlanPrice extends PlanPriceLike {
  currency: string;
}

interface ApiPlan {
  planId: string;
  name: string;
  description: string;
  features: string[];
  sortOrder: number;
  currentPrice: ApiPlanPrice;
}

export interface ManagePlanDialogData {
  plans: ApiPlan[];
  currentPlanName: string;
  currency?: string;
  hasPaymentMethod?: boolean;
  /** False for a published-but-not-yet-activated restaurant — the plan grid still
   * shows for comparison, but every upgrade/downgrade button stays disabled. */
  canManagePlans?: boolean;
  /** Restaurant's active hardware/POS provider — Premium isn't offered on Square,
   * see buildPlans(). */
  posProvider?: string;
  /** Subscription's active-promotion end (epoch seconds), if any. While a promo is
   * still covering billing, a plan change isn't actually charged yet, so a payment
   * method isn't required — see selectPlan(). */
  promoEndsAt?: number | null;
  /** Scheduled downgrade target, if any (FNF-1297). */
  pendingPlanId?: string | null;
  /** When a downgrade chosen now would apply, already formatted in the restaurant's
   * timezone (the current period's end). */
  downgradeEffectiveLabel?: string;
}

export const NEEDS_PAYMENT_METHOD = 'needs-payment-method';

/** Temporarily keep Premium out of Manage Plans for every restaurant, not just
 * Square ones. Set back to false to offer Premium again. */
export const PREMIUM_PLAN_HIDDEN = true;

@Component({
  selector: 'app-manage-plan',
  standalone: true,
  imports: [CommonModule, MatDialogContent],
  templateUrl: './manage-plan.component.html',
  styleUrl: './manage-plan.component.css',
})
export class ManagePlanComponent {
  readonly dialogRef = inject(MatDialogRef<ManagePlanComponent>);
  private readonly dialog = inject(MatDialog);
  private readonly data = inject<ManagePlanDialogData>(MAT_DIALOG_DATA);
  protected readonly getCurrencySymbol = getCurrencySymbol;
  currency = this.data?.currency || 'USD';
  canManagePlans = this.data?.canManagePlans ?? true;
  downgradeEffectiveLabel = this.data?.downgradeEffectiveLabel;
  plans: Plan[] = this.buildPlans(
    this.data?.plans || [],
    this.data?.currentPlanName || '',
    this.data?.posProvider,
  );

  private buildPlans(apiPlans: ApiPlan[], currentPlanName: string, posProvider?: string): Plan[] {
    // Premium isn't offered as a new choice while PREMIUM_PLAN_HIDDEN is on, nor on
    // Square restaurants (its hardware-dependent features — tables, stations,
    // devices, ... — aren't supported on Square yet). A restaurant already on
    // Premium still sees it, so its current plan is never hidden out from under it.
    const hidePremium = PREMIUM_PLAN_HIDDEN || posProvider === 'square';
    const availablePlans = hidePremium
      ? apiPlans.filter(
          (p) =>
            p.name.toLowerCase() !== 'premium' ||
            p.name.toLowerCase() === currentPlanName.toLowerCase(),
        )
      : apiPlans;
    const currentPlan = apiPlans.find(
      (p) => p.name.toLowerCase() === currentPlanName.toLowerCase(),
    );
    const pendingPlanId = this.data?.pendingPlanId;
    const sortedPlans = [...availablePlans].sort((a, b) => a.sortOrder - b.sortOrder);

    return sortedPlans.map((apiPlan, index) => {
      const isActive = apiPlan.name.toLowerCase() === currentPlanName.toLowerCase();
      const price = apiPlan.currentPrice.amountCents / 100;
      // Same rule as subscription-service: monthly amount, then sortOrder. Per-order
      // prices all carry amountCents 0, so for them sortOrder decides.
      const isUpgrade = planChangeDirection(currentPlan, apiPlan) !== 'downgrade';
      const isPendingTarget = !isActive && apiPlan.planId === pendingPlanId;
      const isPaid = isPaidPrice(apiPlan.currentPrice);

      return {
        planId: apiPlan.planId,
        name: apiPlan.name,
        isActive,
        // Recommended-plan status has no backend field to drive it either — plan
        // tier is a free-text `name`, same reasoning as the sidebar/route gating
        // elsewhere in this app (see subscription-plan-menu.util.ts).
        isRecommended: !isActive && apiPlan.name.toLowerCase() === 'premium',
        description: apiPlan.description,
        price,
        priceLabel: priceLabel(apiPlan.currentPrice),
        isPaid,
        prorated:
          isUpgrade && apiPlan.currentPrice.pricingModel !== 'PER_ORDER_WITH_FLAT_CAP' && price > 0,
        isPendingTarget,
        features: this.planFeatures(apiPlan),
        previousPlanName: index > 0 ? sortedPlans[index - 1].name : undefined,
        buttonLabel: isActive
          ? 'Your Current Plan'
          : isPendingTarget
            ? 'Scheduled'
            : isUpgrade
              ? `Upgrade to ${apiPlan.name}`
              : `Downgrade to ${apiPlan.name}`,
        buttonStyle: isActive ? 'outline' : isUpgrade ? 'dark' : 'downgrade',
        buttonDisabled: isActive || isPendingTarget || !this.canManagePlans,
        timing: isActive ? undefined : isUpgrade ? 'upgrade' : 'downgrade',
      };
    });
  }

  /** The sidebar menus a downgrade locks, by the names the restaurant sees in its
   * sidebar, from the same lists that lock them. */
  menusLockedByDowngrade(fromPlanName: string, toPlanName: string): string[] {
    const lockedNow = menuTitlesLockedForPlan(fromPlanName);
    return menuTitlesLockedForPlan(toPlanName).filter((t) => !lockedNow.includes(t));
  }

  /** A downgrade is already scheduled, so the current plan ends at period end. */
  get hasPendingDowngrade(): boolean {
    return this.plans.some((p) => p.isPendingTarget);
  }

  private planFeatures(apiPlan: ApiPlan): string[] {
    return [...apiPlan.features, ...planFeatureLabels(menuTitlesAddedByPlan(apiPlan.name))];
  }

  constructor(private toastr: ToastrService) {
    // Fit the dialog to the cards: two 340px cards (Premium hidden) in the
    // 1100px three-card width left wide empty margins on both sides.
    this.dialogRef.updateSize(this.plans.length > 2 ? '1100px' : '820px');
  }

  close() {
    this.dialogRef.close();
  }

  private regularPrice(plan: Plan): number {
    return plan.originalPrice ?? plan.price;
  }

  /** Whether an active promotion is still covering billing — mirrors the backend's
   * _require_payment_method_if_priced(), so nothing is actually charged yet and a
   * payment method isn't required to switch plans. */
  private get promoCoversBilling(): boolean {
    const promoEndsAt = this.data?.promoEndsAt;
    return !!promoEndsAt && promoEndsAt > Date.now() / 1000;
  }

  selectPlan(plan: Plan) {
    if (plan.buttonDisabled) {
      return;
    }
    if (plan.isPaid && !this.data.hasPaymentMethod && !this.promoCoversBilling) {
      this.toastr.info(
        'Add a payment method before selecting a paid plan.',
        'Payment method needed',
      );
      this.dialogRef.close(NEEDS_PAYMENT_METHOD);
      return;
    }
    const activePlan = this.plans.find((p) => p.isActive) ?? plan;
    const isUpgrade = plan.timing !== 'downgrade';
    const lostFeatures = this.menusLockedByDowngrade(activePlan.name, plan.name);

    const confirmRef = this.dialog.open(ChangePlanConfirmationComponent, {
      width: '480px',
      data: isUpgrade
        ? {
            planId: plan.planId,
            direction: 'upgrade',
            fromPlanName: activePlan.name,
            fromPrice: this.regularPrice(activePlan),
            toPlanName: plan.name,
            toPrice: this.regularPrice(plan),
            fromPriceLabel: activePlan.priceLabel,
            toPriceLabel: plan.priceLabel,
            prorated: plan.prorated,
          }
        : {
            planId: plan.planId,
            direction: 'downgrade',
            fromPlanName: activePlan.name,
            fromPrice: this.regularPrice(activePlan),
            toPlanName: plan.name,
            toPrice: this.regularPrice(plan),
            fromPriceLabel: activePlan.priceLabel,
            toPriceLabel: plan.priceLabel,
            lostFeatures,
            effectiveLabel: this.downgradeEffectiveLabel,
          },
    });

    // The confirmation dialog performs the actual plan-change request itself and only closes
    // (with `true`) once it succeeds — so by the time we get here, it's already done.
    confirmRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.dialogRef.close(true);
      }
    });
  }
}
